import React, {FC, ReactNode, useCallback, useMemo, useState} from "react"
import {Dropdown, Popup, TableCell, DropdownItemProps, DropdownProps, Icon} from "semantic-ui-react"
import {I18nIndividual as GedcomIndividual, Sex} from "../model/gedcom-individual"
import {I18nFamily as GedcomFamily} from "../model/gedcom-family"

/* ------------------------------------------------------------------------------------------------------------------ */
/* Dropdown items                                                                                                     */
/* ------------------------------------------------------------------------------------------------------------------ */
export const sexOptions = [
    { key: "none", value: undefined, tooltiptype: "none" }, // Sentinel
    { key: Sex.M,  value: "M", content: <Icon name="male" /> },
    { key: Sex.F,  value: "F", content: <Icon name="female" /> }
]

interface TooltipPopupProps {
    sex?: Sex
    trigger: ReactNode
    content?: ReactNode
    position?: any
    offset?: [number, number]
}

export const TooltipPopup: FC<TooltipPopupProps> = ({ sex, trigger, content, position, offset }) => {
    if (!content) return <>{trigger}</>
    return (
        <Popup
            wide
            inverted
            hoverable
            size="large"
            offset={offset ?? [0, -5]}
            position={position ?? (sex === Sex.M ? "left center" : "right center")}
            trigger={trigger}
            content={content}
        />
    )
}

interface RelatedOption {
    key: string
    value?: string
    text: string
    fullname?: string
    disabled?: boolean
    sex?: Sex
    tooltiptype: "none" | "self" | "normal"  // Lowercase to be recognized in the DOM as a custom attribute
}

export const useRelatedIndividualOptions = (
    data: GedcomIndividual[],
    id: string | null,
    sex: Sex,
    fullNames: Record<string, string>
): RelatedOption[] => {
    return useMemo(() => {
        const sentinel: RelatedOption = { key: "none", value: undefined, text: "", tooltiptype: "none" }
        const options: RelatedOption[] = data
            .filter(indi => indi.sex === sex)
            .map(indi => ({
                key: indi.id,
                value: indi.id,
                text: indi.id,
                fullname: fullNames[indi.id],
                disabled: indi.id === id || !indi.enabled,
                sex,
                tooltiptype: indi.id === id ? "self" : "normal",
            }))
        return [sentinel, ...options]
    }, [data, id, sex, fullNames])
}

/* ------------------------------------------------------------------------------------------------------------------ */
/* Custom select column                                                                                               */
/* ------------------------------------------------------------------------------------------------------------------ */
interface SelectCellProps {
    field: keyof GedcomIndividual | keyof GedcomFamily
    rowIndex: number
    options: DropdownItemProps[]
    placeholder: string
    onChange: (rowIndex: number, field: keyof GedcomIndividual | keyof GedcomFamily, value: any) => void
    value?: string
    cellClassName?: string
    dropdownClassName?: string
    disabled?: boolean
}

export const SelectCell: FC<SelectCellProps> = ({ field, rowIndex, options, placeholder, onChange, value, dropdownClassName, cellClassName, disabled }) => {
    const [open, setOpen] = useState(false)

    const handleOpen = useCallback(() => setOpen(true), [])
    const handleClose = useCallback(() => setOpen(false), [])

    const handleChange = useCallback(
        (_: React.SyntheticEvent<HTMLElement>, data: DropdownProps) => onChange(rowIndex, field, data.value ?? undefined),
        [rowIndex, field, onChange]
    )

    const optionsMap = useMemo(() => {
        const map = new Map<any, DropdownItemProps>()
        for (const opt of options) if (opt.value !== undefined) map.set(opt.value, opt)
        return map
    }, [options])

    const selectedOption = value !== undefined ? optionsMap.get(value) : undefined

    const individualSex = useMemo(() => {
        if (["fatherId", "husbandId"].includes(field as string)) return Sex.M
        if (["motherId", "wifeId"].includes(field as string)) return Sex.F
        return undefined
    }, [field])


    const dropDown = useMemo(() => (
        <Dropdown
            key={value ?? undefined}
            selection
            clearable
            open={open}
            onOpen={handleOpen}
            onClose={handleClose}
            disabled={disabled || false}
            options={ open
                ? options
                : selectedOption
                    ? [selectedOption]
                    : []  // Lazy loading
            }
            className={`${dropdownClassName || ""} ${selectedOption?.disabled ? "disabled" : ""}`}
            value={value ?? undefined}
            placeholder={placeholder}
            onChange={handleChange}
        />
    ), [open, handleOpen, handleClose, selectedOption, options, disabled, dropdownClassName, value, placeholder, handleChange])

    const tooltipContent = useMemo(() => {
        if (!individualSex || !selectedOption || !("fullname" in selectedOption)) return undefined
        const className = individualSex === Sex.M ? "tooltip male" : "tooltip female"
        return <p className={className}>{selectedOption.fullname}</p>
    }, [individualSex, selectedOption])

    return (
        <TableCell className={cellClassName}>
            {individualSex ? (
                <TooltipPopup
                    sex={individualSex}
                    trigger={dropDown}
                    content={tooltipContent}
                    position={`top ${individualSex === Sex.M ? "left" : "right"}`}
                />
            ) : dropDown}
        </TableCell>
    )
}